import { useQuery } from '@apollo/client';
import * as icons from '@mui/icons-material';
import { CircularProgress, IconButton, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import { TooltipIconButton } from 'components/common/icon/tooltip-icon-button';
import { Table } from 'components/common/table';
import { CompanyIdByCampaignQuery } from 'components/views/_shared-queries/campaign.graphql';
import { CreateOrEditExperimentDialog } from 'components/views/campaign/outreach-template-experiments/create-or-edit-experiment.dialog';
import {
  SchedulerFlowExperimentsQuery,
  SchedulerFlowsForExperimentsQuery,
} from 'components/views/campaign/outreach-template-experiments/outreach-template-experiments.graphql';
import { OutreachTemplatesContext } from 'components/views/campaign/outreach-template-experiments/outreach-templates-context-props';
import { useNavTableConnection } from 'hooks/use-nav-table-connection';
import moment from 'moment';
import { FC, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import routes from 'routes';
import {
  CompanyIdByCampaign,
  CompanyIdByCampaignVariables,
} from 'types/apollo/CompanyIdByCampaign';
import {
  SchedulerFlowExperiments,
  SchedulerFlowExperiments_schedulerFlowExperiments,
  SchedulerFlowExperimentsVariables,
} from 'types/apollo/SchedulerFlowExperiments';
import {
  SchedulerFlowsForExperiments,
  SchedulerFlowsForExperiments_schedulerFlowsByCompanyId,
  SchedulerFlowsForExperimentsVariables,
} from 'types/apollo/SchedulerFlowsForExperiments';

type SchedulerFlow = {
  id: number;
  name: string;
};

type BaseVolumeProps = 'schedulerFlowA' | 'schedulerFlowB' | 'schedulerFlowC' | 'schedulerFlowD';

type OutreachTemplateType = {
  [K in BaseVolumeProps]: SchedulerFlow;
};

const urlAndRender = (
  outreachTemplate: keyof OutreachTemplateType,
  campaignId: string,
  companyId: string,
) => {
  return {
    id: outreachTemplate,
    renderer: (row: SchedulerFlowExperiments_schedulerFlowExperiments) => {
      const name = row[outreachTemplate]
        ? `${row[outreachTemplate]?.name ?? '(no name)'} (${row[outreachTemplate + 'Volume']}%)`
        : '(none)';
      return row[outreachTemplate] ? (
        <Stack direction="row">
          <Link
            to={routes.absolute.outreachTemplate.resolve({
              companyId,
              outreachTemplateId: row[outreachTemplate + 'Id'],
            })}
          >
            {name}
          </Link>
          <Link
            to={routes.company.campaign.conversationWithOutreachTemplateId.resolve({
              companyId,
              campaignId,
              outreachTemplateId: row[outreachTemplate + 'Id'],
            })}
            target="_blank"
          >
            <IconButton color="primary" aria-label="Open conversations">
              <icons.Chat />
            </IconButton>
          </Link>
        </Stack>
      ) : (
        ''
      );
    },
  };
};

const formatDateRange = (start: Date | null, end: Date | null) => {
  const currentDate = moment.utc();
  const dateFormat = 'M/D/YYYY';
  const startDate = moment.utc(start).startOf('day');
  const endDate = moment.utc(end).endOf('day');

  if (endDate.isBefore(startDate)) {
    return 'Inactive forever';
  }
  if (start && startDate.isAfter(currentDate)) {
    return `Not active until ${startDate.format(dateFormat)}`;
  }
  if (end && endDate.isBefore(currentDate)) {
    return `Ended on ${endDate.format(dateFormat)}`;
  }

  if (end) {
    return `Active thru ${endDate.format(dateFormat)}`;
  }
  return 'Active forever';
};

export const CompanyOutreachTemplateExperiments: FC = () => {
  const navigate = useNavigate();
  const [options, setOptions] = useState<SchedulerFlowsForExperiments_schedulerFlowsByCompanyId[]>(
    [],
  );
  const { campaignId, id: companyId } = useParams();
  const [rows, setRows] = useState([] as SchedulerFlowExperiments_schedulerFlowExperiments[]);

  const { data } = useQuery<CompanyIdByCampaign, CompanyIdByCampaignVariables>(
    CompanyIdByCampaignQuery,
    {
      variables: {
        campaignId: campaignId ?? '',
      },
    },
  );

  const { refetch: refetchCompanySchedulerFlows } = useQuery<
    SchedulerFlowsForExperiments,
    SchedulerFlowsForExperimentsVariables
  >(SchedulerFlowsForExperimentsQuery, {
    skip: true,
    onCompleted: (d) => {
      if (d?.schedulerFlowsByCompanyId) {
        setOptions(d.schedulerFlowsByCompanyId);
      }
    },
  });

  const { loading, refetch: refetchExperiments } = useQuery<
    SchedulerFlowExperiments,
    SchedulerFlowExperimentsVariables
  >(SchedulerFlowExperimentsQuery, {
    variables: {
      campaignShortId: campaignId ?? '',
    },
    onCompleted: (d) => {
      if (companyId) {
        refetchCompanySchedulerFlows({ companyId });
      }
      if (d?.schedulerFlowExperiments) {
        setRows(d.schedulerFlowExperiments);
      }
    },
  });

  const campaignNumericId = data?.campaign?.id;

  const tableData = useNavTableConnection<SchedulerFlowExperiments_schedulerFlowExperiments>([
    {
      id: 'id',
      label: 'ID',
      numeric: true,
    },
    {
      label: 'Scheduler Flow A',
      ...urlAndRender('schedulerFlowA', campaignId ?? '', companyId ?? ''),
    },
    {
      label: 'Scheduler Flow B',
      ...urlAndRender('schedulerFlowB', campaignId ?? '', companyId ?? ''),
    },
    {
      label: 'Scheduler Flow C',
      ...urlAndRender('schedulerFlowC', campaignId ?? '', companyId ?? ''),
    },
    {
      label: 'Scheduler Flow D',
      ...urlAndRender('schedulerFlowD', campaignId ?? '', companyId ?? ''),
    },
    {
      id: 'start',
      label: 'Status',
      renderer: (row: SchedulerFlowExperiments_schedulerFlowExperiments) =>
        formatDateRange(row.start, row.end),
    },
    {
      id: 'end',
      label: '',
      renderer: (row: SchedulerFlowExperiments_schedulerFlowExperiments) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CreateOrEditExperimentDialog key={row.id} data={row} refetch={refetchExperiments} />
          {row.hasMetrics && (
            <TooltipIconButton
              tooltip="View results"
              onClick={() =>
                navigate(
                  `${routes.company.campaign.resolve({
                    id: companyId ?? '',
                    campaignId: campaignId ?? '',
                  })}/${routes.company.campaign.experiments.path}?experimentId=${row.id}`,
                )
              }
              iconButtonProps={{ size: 'small', sx: { color: 'text.primary' } }}
            >
              <icons.BarChart fontSize="small" />
            </TooltipIconButton>
          )}
        </Stack>
      ),
    },
  ]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ paddingY: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <OutreachTemplatesContext.Provider value={{ options }}>
      <Box p={2}>
        {campaignNumericId && (
          <CreateOrEditExperimentDialog
            data={null}
            campaignId={campaignNumericId}
            refetch={refetchExperiments}
          />
        )}
      </Box>
      <Table rows={rows} {...tableData} />
    </OutreachTemplatesContext.Provider>
  );
};
